import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import { useTheme } from '@mui/material'
import { ChecklistTab } from '@modules/checklistDetails/tabs/ChecklistTab'
import { ChecklistTimelineTab } from '@modules/checklistDetails/tabs/ChecklistTimelineTab'
import {
  ArrowDropDown,
  Download,
  FileCopy,
  InfoOutlined,
  IosShare,
  PersonAddAlt1Outlined,
} from '@mui/icons-material'
import { Box, Flex } from '@fundamentals'
import { Button, Chip, DropdownButton, IconButton } from '@common/material'
import { NavigationAwareTabs } from '@elements'
import { TemplateVersionTitle } from '@elements/TemplateVersionTitle'
import { ChecklistDetailsSidebar } from '@modules/checklistDetails/ChecklistDetailsSidebar'
import ClearIcon from '@mui/icons-material/Clear'
import { useModal } from '@common/GlobalModal'
import {
  useChecklistPdfDownloadUrl,
  useCloneChecklist,
  useGetChecklistById,
  useGetChecklistLinkedProcesses,
  useGetChecklistLinkedTasks,
} from '@core/react-query/features/checklists/checklist'
import { useRouter } from 'next/router'
import { PageUrls } from '@core/page-urls'
import { BetaChecklistModal } from '@modules/checklistDetails/BetaChecklistModal'
import { useToast } from '@core/toast'
import { ChecklistListTabs } from '@core/entities/checklist/Checklist/types'
import {
  CommentsContext,
  createCommentsContextValue,
} from '@core/react-query/features/comments/hooks/CommentsContext'
import { TotalCommentsChip } from '@elements/Comments'
import { ObjectDetectionRobotChip } from '@elements/ObjectDetection/ObjectDetectionRobotChip'
import LaunchIcon from '@mui/icons-material/Launch'
import { BriefingAddParticipantsModal } from '@modules/briefings'
import { useRealTimeChecklist } from '@core/react-query/features/checklists/checklist/hooks/useRealTimeChecklist'
import { LinkedDocumentsList } from '@elements/LinkingProcesses/LinkedDocumentsList'
import LinkIcon from '@mui/icons-material/Link'
import { ShareQRCodeModal } from '@common/ShareQRCodeModal'
import Project from '@core/project'
import { LinkedRegisterItemsGroupedByRegister } from '@common/LinkedRegisterItemsGroupedByRegister'
import { Palette } from '@core/theme/color-palette-siteassist'
import { useProductAccess } from 'webapp/providers/authentication'
import useConstants from '@core/providers/useConstants'
import useFeatureFlags from '@core/providers/useFeatureFlags'

type ChecklistDetailsActionsType = {
  icon: React.ReactElement
  name: string
  dataTest?: string
  onClick: () => void
  divider?: boolean
}

type ComponentType = {
  checklistId: number
  isModal?: boolean
  organisationId: number
  projectId?: number
  isUsedForInspection?: boolean
}

const ChecklistDetails: React.FC<ComponentType> = ({
  checklistId,
  isModal = false,
  organisationId,
  projectId,
  isUsedForInspection = false,
}) => {
  const theme = useTheme()
  const { hideModal, showModal } = useModal()
  const router = useRouter()
  const { isViewMode } = useProductAccess()
  const bgColor = isModal ? Palette.Common.White : Palette.Grey[50]

  const { data: checklist, isFetching } = useGetChecklistById({ checklistId })
  useRealTimeChecklist(checklistId, {
    enabled: !isFetching,
  })
  const downloadMutation = useChecklistPdfDownloadUrl({
    onError: (e) => toast.error(e.message),
  })
  const cloneChecklist = useCloneChecklist()
  const { flags } = useFeatureFlags(['beta'])
  const toast = useToast()
  const { constants } = useConstants()
  const navBarHeight = constants.nav_bar_height

  const commentsContext = createCommentsContextValue({
    commentsPayload: {
      id: checklistId,
      type: 'CHECKLIST',
      size: 1000,
    },
    projectId,
  })

  const { data } = useGetChecklistLinkedProcesses(
    {
      checklistId,
    },
    {
      onError: (e) => toast.error(e.message),
    },
  )

  // todo: remove this once the backend returns task in the linkedProcess endpoint
  const { data: tasks } = useGetChecklistLinkedTasks(
    {
      checklistId,
    },
    {
      onError: (e) => toast.error(e.message),
    },
  )

  const linkedPermits = data?.getLinkedPermits() || []
  const linkedChecklists = data?.getLinkedChecklists() || []
  const linkedInspections = data?.getLinkedInspections() || []
  const linkedRegisterItemsGroupedByRegister =
    data?.getLinkedProcessItemsByRegister() || []
  const linkedTasks = tasks || []

  const tabs = useMemo(() => {
    const tabBuilder = NavigationAwareTabs.TabBuilder().addTab({
      name: localizedStrings.checklist,
      label: isUsedForInspection
        ? localizedStrings.inspection
        : localizedStrings.checklist,
      component: <ChecklistTab checklist={checklist} />,
    })

    tabBuilder.addTab({
      name: localizedStrings.timeline,
      label: localizedStrings.timeline,
      clearParams: true,
      component: <ChecklistTimelineTab checklistId={checklistId} />,
    })

    if (linkedPermits.length) {
      tabBuilder.addTab({
        name: 'Permits',
        icon: <LinkIcon />,
        label: localizedStrings.permits,
        badgeCount: linkedPermits.length,
        component: (
          <LinkedDocumentsList
            items={linkedPermits}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedChecklists.length) {
      tabBuilder.addTab({
        name: 'Checklists',
        icon: <LinkIcon />,
        label: localizedStrings.checklists,
        badgeCount: linkedChecklists.length,
        component: (
          <LinkedDocumentsList
            items={linkedChecklists}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedInspections.length) {
      tabBuilder.addTab({
        name: 'Inspections',
        icon: <LinkIcon />,
        label: localizedStrings.inspections,
        badgeCount: linkedInspections.length,
        component: (
          <LinkedDocumentsList
            items={linkedInspections}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedTasks.length) {
      tabBuilder.addTab({
        name: 'Tasks',
        icon: <LinkIcon />,
        label: localizedStrings.tasks,
        dataTest: 'checklist-tasks-tab',
        badgeCount: linkedTasks.length,
        component: (
          <LinkedDocumentsList
            items={linkedTasks}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedRegisterItemsGroupedByRegister.length) {
      tabBuilder.addTab({
        name: 'Register Items',
        icon: <LinkIcon />,
        label: localizedStrings.registerItems,
        badgeCount: linkedRegisterItemsGroupedByRegister.length,
        component: (
          <LinkedRegisterItemsGroupedByRegister
            linkedRegisterItemsGroupedByRegister={
              linkedRegisterItemsGroupedByRegister
            }
            organisationId={organisationId}
          />
        ),
      })
    }

    return tabBuilder.build()
  }, [checklist, data, tasks])

  const handleDownload = async () => {
    const pdfDownloadUrl = await downloadMutation.mutateAsync({
      checklistId,
    })

    const toastId = Utils.GUID()
    toast.progress(
      isUsedForInspection
        ? localizedStrings.downloadingInspection
        : localizedStrings.downloadingChecklist,
      {
        duration: 80 * 1000,
        id: toastId,
      },
    )

    const fileGenerated = await Utils.pollForSuccessfulRequest(
      pdfDownloadUrl.urlDownload,
    )

    if (!fileGenerated) {
      toast.error(
        isUsedForInspection
          ? localizedStrings.inspectionDownloadFailed
          : localizedStrings.checklistDownloadFailed,
        { id: toastId },
      )
    } else {
      window.location.assign(pdfDownloadUrl.urlDownload)
      toast.success(
        isUsedForInspection
          ? localizedStrings.inspectionDownloadComplete
          : localizedStrings.checklistDownloadComplete,
        { id: toastId },
      )
    }
  }

  const handleClone = () => {
    cloneChecklist.mutate(
      {
        checklistId,
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.clonedToNewDraft)
          router.push(
            PageUrls.checklists(checklist.organisationId, {
              tab: ChecklistListTabs.DRAFTS,
            }),
          )
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const secondaryActionsOptionsMemoised = useMemo(() => {
    const secondaryActionsOptions: ChecklistDetailsActionsType[] = []

    secondaryActionsOptions.push({
      name: localizedStrings.share,
      icon: <IosShare />,
      dataTest: 'share',
      divider: true,
      onClick: () => {
        const organisationId = checklist.organisationId
        const projectId = checklist.project.id
        const checklistId = checklist.id
        const inspectionUrl = PageUrls.projectInspection({
          organisationId,
          projectId,
          checklistId,
        })
        const checklistUrl = PageUrls.projectChecklistDetail({
          organisationId,
          projectId,
          checklistId,
        })
        const entityUrl = `${Project.appUrl}${
          checklist.isInspection ? inspectionUrl : checklistUrl
        }`

        showModal(
          <ShareQRCodeModal
            title={localizedStrings.share}
            entityUrl={entityUrl}
            entityLinkLabel={localizedStrings.link}
            entityLinkDescription={localizedStrings.copyLinkDescription}
            pdfGenerationFailedMessage={localizedStrings.pdfGenerationFailed}
            pdfMutation={downloadMutation}
            pdfMutationParams={{ checklistId }}
          />,
        )
      },
    })

    const downloadAction: ChecklistDetailsActionsType = {
      name: isUsedForInspection
        ? localizedStrings.downloadInspection
        : localizedStrings.downloadChecklist,
      icon: <Download />,
      dataTest: 'downloadChecklist',
      onClick: handleDownload,
    }

    const cloneAction: ChecklistDetailsActionsType = {
      name: localizedStrings.clone,
      icon: <FileCopy />,
      dataTest: 'clone-permit',
      onClick: () => handleClone(),
    }

    secondaryActionsOptions.push(downloadAction)
    if (checklist?.permittedActions?.includes('CLONE')) {
      secondaryActionsOptions.push(cloneAction)
    }

    if (checklist?.hasPermission('BRIEF_PARTICIPANTS')) {
      secondaryActionsOptions.push({
        name: localizedStrings.addParticipants,
        icon: <PersonAddAlt1Outlined />,
        dataTest: 'brief-participant',
        onClick: () =>
          showModal(
            <BriefingAddParticipantsModal
              entityId={checklist.id}
              entityType='CHECKLIST'
            />,
          ),
      })
    }

    const beta: ChecklistDetailsActionsType = {
      name: 'Beta',
      icon: <InfoOutlined />,
      dataTest: 'beta',
      onClick: () => {
        showModal(<BetaChecklistModal checklist={checklist} />)
      },
    }

    flags.beta && secondaryActionsOptions.push(beta)

    return secondaryActionsOptions
  }, [handleDownload])

  const stickyPosition = {
    HEADER: isModal ? 0 : `calc(${navBarHeight}px + 16px)`,
    TAB_HEADER: isModal ? '100px' : '156px',
  }

  return (
    !!checklist && (
      <CommentsContext.Provider value={commentsContext}>
        <Box
          data-test='checklist-details'
          top={stickyPosition.HEADER}
          zIndex={10}
          position='sticky'
          bgcolor={bgColor}
          px='3'
          pb={1}
        >
          <Flex alignItems='center' mb={1.5}>
            <TemplateVersionTitle
              dataTest='checklist-details-title'
              organisationId={checklist.organisationId}
              template={checklist.template}
              reference={checklist.shortUUID}
              getTemplateUrl={PageUrls.editChecklistTemplate}
              getVersionUrl={PageUrls.editChecklistTemplateVersion}
            />
            {isModal && !isViewMode && (
              <Button
                sx={{ ml: 2 }}
                startIcon={<LaunchIcon />}
                onClick={() => {
                  const projectId = checklist?.project?.id
                  const inspectionUrl = projectId
                    ? PageUrls.projectInspection({
                        organisationId,
                        projectId,
                        checklistId,
                      })
                    : PageUrls.inspection(organisationId, checklistId)
                  const checklistUrl = projectId
                    ? PageUrls.projectChecklistDetail({
                        organisationId,
                        projectId,
                        checklistId,
                      })
                    : PageUrls.checklist(organisationId, checklistId)
                  window.open(
                    checklist?.isInspection ? inspectionUrl : checklistUrl,
                    '_blank',
                    'noopener, noreferrer',
                  )
                }}
              >
                {localizedStrings.openInNewTab}
              </Button>
            )}
            <Box flexGrow={1} />
            {isModal && (
              <IconButton aria-label='close-modal' onClick={hideModal}>
                <ClearIcon />
              </IconButton>
            )}
          </Flex>
          <Flex flexDirection='row' gap={1} alignItems={'center'}>
            <Chip
              color={checklist.getStatusChipColor()}
              label={checklist.getStatusString()}
              data-test='checklist-status-chip'
            />
            {checklist.getSecondaryStatusString() && (
              <Chip
                variant='outlined'
                label={checklist.getSecondaryStatusString()}
              />
            )}
            {checklist.getApprovalProgressText() && (
              <Chip
                variant='outlined'
                label={checklist.getApprovalProgressText()}
              />
            )}
            <TotalCommentsChip />
            <ObjectDetectionRobotChip checklist={checklist} />
            <Box flexGrow={1} />
            {!isViewMode && (
              <Box>
                <DropdownButton
                  label={localizedStrings.actions}
                  options={secondaryActionsOptionsMemoised.map(
                    (secondaryOptions) => ({
                      title: secondaryOptions.name,
                      value: secondaryOptions.name,
                      'data-test': secondaryOptions.dataTest,
                      onSelect: secondaryOptions.onClick,
                      icon: secondaryOptions.icon,
                      divider: secondaryOptions.divider,
                    }),
                  )}
                  renderButton={(onClick) => (
                    <Button
                      variant='outlined'
                      data-test='secondary-checklist-actions'
                      endIcon={<ArrowDropDown />}
                      onClick={onClick}
                      sx={{
                        fontSize: 16,
                        background: theme.palette.legacy.white[10],
                        ml: 1,
                      }}
                    >
                      {localizedStrings.actions}
                    </Button>
                  )}
                />
              </Box>
            )}
          </Flex>
        </Box>
        <Flex alignItems='flex-start'>
          <NavigationAwareTabs
            tabs={tabs}
            withoutQueryParam={isModal}
            tabHeaderSx={{
              position: 'sticky',
              top: stickyPosition.TAB_HEADER,
              zIndex: 20,
              bgcolor: bgColor,
            }}
          />

          <Box
            pl={3}
            width='23rem'
            position='sticky'
            top={stickyPosition.TAB_HEADER}
            maxHeight={`calc(100vh - ${stickyPosition.TAB_HEADER})`}
            overflow='auto'
          >
            <ChecklistDetailsSidebar
              checklist={checklist}
              organisationId={organisationId}
              projectId={projectId}
            />
          </Box>
        </Flex>
      </CommentsContext.Provider>
    )
  )
}
export default ChecklistDetails
